import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { API, saveToLocal, loadFromLocal, clearLocal } from "../lib/utils";

const AssessmentContext = createContext(null);

export function AssessmentProvider({ children }) {
  const [user, setUser] = useState(null);
  const [member, setMember] = useState(null);
  const [session, setSession] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState({});
  const [responses, setResponses] = useState({});
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState(null);
  
  const hasRestoredSession = useRef(false);

  // Restore session from local storage and fetch data from backend on mount
  useEffect(() => {
    const restoreSession = async () => {
      if (hasRestoredSession.current) return;
      hasRestoredSession.current = true;
      
      const savedData = loadFromLocal("lifewheel_session");
      if (savedData?.member?.id) {
        try {
          // Verify session is still valid by rejoining
          const response = await axios.get(`${API}/members/${savedData.member.id}/rejoin`);
          
          setUser(response.data.user);
          setMember(response.data.member);
          setSession(response.data.session);
          
          // Fetch assessment data
          const itemsResponse = await axios.get(`${API}/assessment-items`);
          setCategories(itemsResponse.data.categories);
          setItems(itemsResponse.data.items);
          
          // Fetch saved responses from backend
          const responsesResponse = await axios.get(`${API}/members/${savedData.member.id}/responses`);
          const responseMap = {};
          responsesResponse.data.responses.forEach((r) => {
            responseMap[r.item_id] = r;
          });
          setResponses(responseMap);
          
          // Fetch progress
          const progressResponse = await axios.get(`${API}/members/${savedData.member.id}/progress`);
          setProgress(progressResponse.data);
          
          console.log("Session restored successfully");
        } catch (e) {
          console.error("Failed to restore session:", e);
          // Clear invalid session data
          clearLocal("lifewheel_session");
        }
      }
      setInitializing(false);
    };
    
    restoreSession();
  }, []);

  // Save to local storage when session data changes
  useEffect(() => {
    if (user && member && session) {
      saveToLocal("lifewheel_session", { user, member, session });
    }
  }, [user, member, session]);

  // Fetch categories and items
  const fetchAssessmentData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/assessment-items`);
      setCategories(response.data.categories);
      setItems(response.data.items);
    } catch (e) {
      console.error("Error fetching assessment data:", e);
      setError("Failed to load assessment data");
    }
  }, []);

  // Join session (checks for existing user first)
  const joinSession = useCallback(async (inviteCode, displayName) => {
    setLoading(true);
    setError(null);
    try {
      // Use the rejoin-by-code endpoint which handles both new and returning users
      const response = await axios.post(`${API}/sessions/rejoin-by-code`, {
        invite_code: inviteCode,
        display_name: displayName,
      });
      
      setUser(response.data.user);
      setMember(response.data.member);
      setSession(response.data.session);
      
      await fetchAssessmentData();
      
      // If returning user, fetch their existing responses
      if (response.data.is_returning) {
        const responsesResponse = await axios.get(`${API}/members/${response.data.member.id}/responses`);
        const responseMap = {};
        responsesResponse.data.responses.forEach((r) => {
          responseMap[r.item_id] = r;
        });
        setResponses(responseMap);
        
        const progressResponse = await axios.get(`${API}/members/${response.data.member.id}/progress`);
        setProgress(progressResponse.data);
      }
      
      return response.data;
    } catch (e) {
      const message = e.response?.data?.detail || "Failed to join session";
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, [fetchAssessmentData]);

  // Create session (facilitator)
  const createSession = useCallback(async (name, facilitatorName, facilitatorEmail) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/sessions`, {
        name,
        facilitator_name: facilitatorName,
        facilitator_email: facilitatorEmail,
      });
      
      const sessionData = response.data;
      
      // Set user as facilitator
      setUser({
        id: sessionData.facilitator_id,
        display_name: facilitatorName,
        role: "facilitator",
      });
      setSession(sessionData);
      
      await fetchAssessmentData();
      
      return sessionData;
    } catch (e) {
      const message = e.response?.data?.detail || "Failed to create session";
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, [fetchAssessmentData]);

  // Fetch member responses
  const fetchResponses = useCallback(async () => {
    if (!member) return;
    
    try {
      const response = await axios.get(`${API}/members/${member.id}/responses`);
      const responseMap = {};
      response.data.responses.forEach((r) => {
        responseMap[r.item_id] = r;
      });
      setResponses(responseMap);
    } catch (e) {
      console.error("Error fetching responses:", e);
    }
  }, [member]);

  // Fetch progress
  const fetchProgress = useCallback(async () => {
    if (!member) return;
    
    try {
      const response = await axios.get(`${API}/members/${member.id}/progress`);
      setProgress(response.data);
    } catch (e) {
      console.error("Error fetching progress:", e);
    }
  }, [member]);

  // Save response - saves immediately to backend
  const saveResponse = useCallback(async (itemId, rating, q1Text, q2Text) => {
    if (!member) return;
    
    // Update local state immediately for responsiveness
    setResponses((prev) => ({
      ...prev,
      [itemId]: {
        item_id: itemId,
        rating,
        q1_text: q1Text,
        q2_text: q2Text,
      },
    }));
    
    try {
      await axios.post(`${API}/members/${member.id}/responses`, {
        responses: [{
          item_id: itemId,
          rating,
          q1_text: q1Text,
          q2_text: q2Text,
        }],
      });
      
      // Refresh progress
      await fetchProgress();
    } catch (e) {
      console.error("Error saving response:", e);
      throw new Error("Failed to save response");
    }
  }, [member, fetchProgress]);

  // Submit assessment
  const submitAssessment = useCallback(async () => {
    if (!member) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/members/${member.id}/submit`);
      
      // Update member status locally
      const updatedMember = {
        ...member,
        submitted_at: new Date().toISOString(),
        status: "submitted",
      };
      setMember(updatedMember);
      
      // Update local storage
      saveToLocal("lifewheel_session", {
        user,
        member: updatedMember,
        session,
      });
      
      return true;
    } catch (e) {
      const message = e.response?.data?.detail || "Failed to submit assessment";
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, [member, user, session]);

  // Clear session
  const clearSession = useCallback(() => {
    setUser(null);
    setMember(null);
    setSession(null);
    setResponses({});
    setProgress(null);
    clearLocal("lifewheel_session");
  }, []);

  // Get response for item
  const getResponse = useCallback((itemId) => {
    return responses[itemId] || null;
  }, [responses]);

  // Calculate category completion
  const getCategoryCompletion = useCallback((categoryId) => {
    const categoryItems = items[categoryId] || [];
    const completed = categoryItems.filter(
      (item) => responses[item.id]?.rating !== undefined && responses[item.id]?.rating !== null
    ).length;
    return {
      total: categoryItems.length,
      completed,
      percentage: categoryItems.length > 0 ? Math.round((completed / categoryItems.length) * 100) : 0,
    };
  }, [items, responses]);

  const value = {
    user,
    member,
    session,
    categories,
    items,
    responses,
    progress,
    loading,
    initializing,
    error,
    joinSession,
    createSession,
    fetchAssessmentData,
    fetchResponses,
    fetchProgress,
    saveResponse,
    submitAssessment,
    clearSession,
    getResponse,
    getCategoryCompletion,
    setError,
  };

  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  );
}

export function useAssessment() {
  const context = useContext(AssessmentContext);
  if (!context) {
    throw new Error("useAssessment must be used within an AssessmentProvider");
  }
  return context;
}
